import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../Layout";
import ProductCard from "../ProductCard";
import Reveal from "../Reveal";
import Seo from "../Seo";
import { useCart } from "../../context/CartContext";
import { useSettings } from "../../context/SettingsContext";
import { getProduct, getProducts } from "../../api/shop";
import { money, cld, fromPrice } from "../format";
import Reviews from "../Reviews";

// Slide transition for the gallery: enter from the swipe direction, exit the
// opposite way. dir > 0 = moving forward (next image / next shade).
const slideVariants = {
  enter: (dir) => ({ x: dir >= 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir >= 0 ? "-100%" : "100%", opacity: 0 }),
};

const Product = () => {
  const { slug } = useParams();
  const { add } = useCart();
  const settings = useSettings();
  const [product, setProduct] = useState(null);
  const [shades, setShades] = useState([]);
  const [sel, setSel] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [slideDir, setSlideDir] = useState(1);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [filter, setFilter] = useState("");
  const [related, setRelated] = useState([]);
  const touchStart = useRef(null);

  useEffect(() => {
    setFilter("");
    getProduct(slug).then((r) => {
      if (r.error) return setProduct({ error: true });
      setProduct(r.product);
      const active = (r.shades || []).filter((s) => s.status !== "Disabled");
      setShades(active);
      setSel(active[0] || null);
      setActiveImg(0);
      const catId = r.product.category && (r.product.category._id || r.product.category);
      if (catId) {
        getProducts(`?category=${catId}`).then((res) =>
          setRelated(
            (res.products || []).filter((p) => p.slug !== r.product.slug).slice(0, 4)
          )
        );
      }
    });
  }, [slug]);

  const gallery = useMemo(() => {
    if (sel && sel.images && sel.images.length) return sel.images.map((i) => i.url);
    if (product && product.coverImage) return [product.coverImage.url];
    return [];
  }, [sel, product]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return shades;
    return shades.filter((s) => s.name.toLowerCase().indexOf(q) > -1);
  }, [shades, filter]);

  // Select a shade (from a swatch) and reset to its first image.
  const pickShade = (sh, dir = 1) => {
    setSlideDir(dir);
    setSel(sh);
    setActiveImg(0);
  };

  // Swipe navigation across a flattened sequence of every shade's images:
  // advance through the current shade's images, then roll into the next shade.
  const swipe = (dir) => {
    if (!shades.length) return;
    const idx = shades.findIndex((s) => sel && s._id === sel._id);
    if (dir > 0) {
      if (activeImg < gallery.length - 1) {
        setSlideDir(1);
        setActiveImg((i) => i + 1);
      } else {
        const next = shades[(idx + 1) % shades.length];
        if (next && next._id !== sel._id) pickShade(next, 1);
      }
    } else {
      if (activeImg > 0) {
        setSlideDir(-1);
        setActiveImg((i) => i - 1);
      } else {
        const prev = shades[(idx - 1 + shades.length) % shades.length];
        if (prev && prev._id !== sel._id) {
          const lastIdx = Math.max(0, (prev.images || []).length - 1);
          setSlideDir(-1);
          setSel(prev);
          setActiveImg(lastIdx); // land on the previous shade's last image
        }
      }
    }
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) swipe(dx < 0 ? 1 : -1);
  };

  if (!product)
    return <Layout><p className="text-center text-muted py-40">Loading…</p></Layout>;
  if (product.error)
    return <Layout><p className="text-center text-muted py-40">Product not found.</p></Layout>;

  const addToCart = () => {
    if (!sel) return;
    add(
      {
        shadeId: sel._id,
        shadeName: sel.name,
        productName: product.name,
        productSlug: product.slug,
        price: sel.price,
        image: gallery[0] || null,
      },
      qty
    );
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const storeName = settings.storeName || "Aura Rare";
  const siteUrl =
    (process.env.REACT_APP_SITE_URL || "").replace(/\/+$/, "") ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const lowPrice = fromPrice(shades);
  const seoImages = gallery.length
    ? gallery
    : product.coverImage
    ? [product.coverImage.url]
    : [];
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description:
      product.description ||
      `${product.name} by ${storeName}${
        shades.length ? ` — available in ${shades.length} shades.` : "."
      }`,
    image: seoImages,
    brand: { "@type": "Brand", name: storeName },
    category: product.category ? product.category.name : undefined,
    url: siteUrl ? `${siteUrl}/product/${product.slug}` : undefined,
    ...(lowPrice != null && shades.length
      ? {
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "INR",
            lowPrice,
            offerCount: shades.length,
            availability: "https://schema.org/InStock",
          },
        }
      : {}),
    ...(product.rating && product.rating.count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: product.rating.avg,
            reviewCount: product.rating.count,
          },
        }
      : {}),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteUrl || undefined },
      ...(product.category
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: product.category.name,
              item: siteUrl
                ? `${siteUrl}/category/${product.category.slug}`
                : undefined,
            },
          ]
        : []),
      {
        "@type": "ListItem",
        position: product.category ? 3 : 2,
        name: product.name,
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title={product.name}
        description={
          product.description ||
          `${product.name} by ${storeName} — ${
            shades.length ? `${shades.length} curated shades. ` : ""
          }Order easily over WhatsApp.`
        }
        image={seoImages[0]}
        path={`/product/${product.slug}`}
        type="product"
        jsonLd={[productJsonLd, breadcrumbJsonLd]}
      />
      <div className="aura-container py-12 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20">
        {/* Gallery — swipe across all shades' images */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}>
          <div
            className="bg-sand rounded"
            style={{ aspectRatio: "1 / 1", position: "relative", overflow: "hidden", touchAction: "pan-y" }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence initial={false} custom={slideDir}>
              {gallery[activeImg] && (
                <motion.img
                  key={`${sel ? sel._id : "x"}-${activeImg}`}
                  src={cld(gallery[activeImg], 1000)}
                  alt={`${product.name}${sel ? ` — ${sel.name}` : ""}`}
                  custom={slideDir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-full object-cover"
                  style={{ position: "absolute", inset: 0 }}
                  draggable={false}
                />
              )}
            </AnimatePresence>
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setSlideDir(i >= activeImg ? 1 : -1);
                    setActiveImg(i);
                  }}
                  className="w-20 h-20 rounded bg-sand overflow-hidden"
                  style={{
                    boxShadow:
                      i === activeImg ? "0 0 0 2px #fff, 0 0 0 4px var(--accent)" : "none",
                  }}
                >
                  <img src={cld(g, 160)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="md:py-4"
        >
          <div className="eyebrow mb-3">
            {product.category ? product.category.name : "Collection"}
          </div>
          <h1 className="display-1 mb-3">{product.name}</h1>

          {product.rating && product.rating.count > 0 && (
            <div className="mb-4 text-sm" style={{ color: "var(--accent-hover)" }}>
              {"★".repeat(Math.round(product.rating.avg))}
              {"☆".repeat(5 - Math.round(product.rating.avg))}
              <span className="text-muted ml-2">
                {product.rating.avg} · {product.rating.count} review
                {product.rating.count > 1 ? "s" : ""}
              </span>
            </div>
          )}

          {sel && (
            <div className="flex items-end gap-3 mb-8">
              <span className="text-2xl md:text-3xl text-ink">{money(sel.price)}</span>
              {sel.mrp && sel.mrp > sel.price && (
                <span className="price-strike text-lg mb-1">{money(sel.mrp)}</span>
              )}
            </div>
          )}

          {/* Shade selector */}
          <div className="mb-10">
            <div className="flex justify-between items-baseline mb-4">
              <span className="eyebrow">Shade · {shades.length}</span>
              {sel && <span className="text-sm font-medium">{sel.name}</span>}
            </div>
            {shades.length > 12 && (
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Find a shade… e.g. 23 or Red"
                className="lux-input mb-3"
                style={{ padding: "0.6rem 0.9rem" }}
              />
            )}
            <div
              className="grid"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))",
                gap: "0.7rem",
              }}
            >
              {shown.map((sh) => {
                const thumb = sh.images && sh.images[0] && sh.images[0].url;
                // Badge = first two letters of the shade name (e.g. "Maroon
                // Bride 02" -> "Ma"). Numeric-only names with no letters
                // (e.g. Mattitude "04") fall back to their digits.
                const letters = sh.name.replace(/[^A-Za-z]/g, "").slice(0, 2);
                const label = letters || sh.name.replace(/[^0-9]/g, "").slice(-2);
                return (
                  <button
                    key={sh._id}
                    title={sh.name}
                    onClick={() => pickShade(sh)}
                    className={`swatch ${sel && sel._id === sh._id ? "selected" : ""}`}
                    style={thumb ? { backgroundImage: `url(${cld(thumb, 120)})` } : undefined}
                  >
                    {!thumb && label}
                  </button>
                );
              })}
              {shown.length === 0 && (
                <div className="col-span-full text-sm text-muted py-2">
                  No shades match “{filter}”.
                </div>
              )}
            </div>
          </div>

          {/* Qty + Add */}
          {shades.length === 0 ? (
            <div className="mb-8">
              <button className="btn-outline w-full" disabled>
                Currently Unavailable
              </button>
              <p className="text-xs text-muted mt-2 text-center">
                This product is out of stock right now — check back soon.
              </p>
            </div>
          ) : (
            <div className="flex items-stretch gap-4 mb-8">
              <div className="flex items-center border border-hairline rounded">
                <button className="px-4 text-lg" onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
                <span className="px-4">{qty}</span>
                <button className="px-4 text-lg" onClick={() => setQty(qty + 1)}>+</button>
              </div>
              <button className="btn-accent flex-1" onClick={addToCart} disabled={!sel}>
                {added ? "Added ✓" : "Add to Cart"}
              </button>
            </div>
          )}

          {product.description && (
            <div className="hairline-t pt-8">
              <div className="eyebrow mb-3">Details</div>
              <p className="text-muted leading-relaxed">{product.description}</p>
            </div>
          )}
        </motion.div>
      </div>

      {sel && (
        <div className="aura-container pb-12">
          <Reviews shade={sel} />
        </div>
      )}

      {/* Related products */}
      {related.length > 0 && (
        <div className="aura-container pb-24">
          <Reveal className="text-center mb-12 mt-12">
            <div className="eyebrow mb-3">You May Also Like</div>
            <h2 className="display-2">
              More from {product.category ? product.category.name : "the Collection"}
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {related.map((p, i) => (
              <ProductCard key={p._id} product={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Product;
